"use client";

import * as React from "react";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import {
	Command,
	CommandEmpty,
	CommandInput,
	CommandItem,
	CommandList,
} from "./command";
import {
	DrawerClose,
	DrawerContent,
	DrawerFooter,
	DrawerNestedRoot,
	DrawerTitle,
	DrawerTrigger,
} from "./drawer";
import { FilterRow } from "./filter-row";
import {
	type MultiSelectFilterOption,
	type MultiSelectFilterProps,
} from "./multi-select-filter";

/**
 * Drawer-based multi/single-select filter for mobile / small viewports.
 *
 * Always renders as a {@link FilterRow} trigger + nested {@link DrawerNestedRoot}
 * with a full-width checkbox list, optional search, sticky header (label + Clear)
 * and sticky footer ("Done"). This component does NOT do any responsive
 * switching — pair it with `MultiSelectFilter` at the callsite (e.g. via a
 * small app-level wrapper) when you need a popover variant on desktop.
 *
 * Props mirror {@link MultiSelectFilterProps} so it can be a drop-in.
 */
export function FilterDrawerSelect<T extends string | number>(
	props: MultiSelectFilterProps<T>,
) {
	const {
		label,
		options,
		selected,
		onChange,
		multiSelect = true,
		disabled = false,
		className,
		enableSearch = false,
		searchPlaceholder = "Search options...",
		noOptionsText = "No options found.",
	} = props;

	const isMulti = multiSelect !== false;
	const [open, setOpen] = React.useState(false);

	const isOptionSelected = React.useCallback(
		(option: MultiSelectFilterOption<T>) => {
			if (selected === null) {
				return false;
			}

			if (!isMulti && !Array.isArray(selected)) {
				if (Array.isArray(option.value)) {
					return option.value.length > 0 && option.value[0] === selected;
				}
				return option.value === selected;
			}

			const selectedArray = Array.isArray(selected)
				? selected
				: [selected as T];

			if (Array.isArray(option.value)) {
				return option.value.every((v) => selectedArray.includes(v));
			}
			return selectedArray.includes(option.value as T);
		},
		[selected, isMulti],
	);

	const handleSelect = (option: MultiSelectFilterOption<T>) => {
		if (isMulti) {
			const selectedArray = Array.isArray(selected)
				? selected
				: selected !== null
					? [selected as T]
					: [];

			if (isOptionSelected(option)) {
				const optionValue = option.value;
				const next = Array.isArray(optionValue)
					? selectedArray.filter((v) => !optionValue.includes(v))
					: selectedArray.filter((v) => v !== optionValue);

				(onChange as (next: T[] | null) => void)(
					next.length === 0 ? null : next,
				);
				return;
			}

			const optionValue = option.value;
			const next = Array.isArray(optionValue)
				? Array.from(new Set([...selectedArray, ...optionValue]))
				: [...selectedArray, optionValue];
			(onChange as (next: T[] | null) => void)(next);
			return;
		}

		if (Array.isArray(option.value)) {
			(onChange as (next: T | null) => void)(
				option.value.length > 0 ? (option.value[0] as T) : null,
			);
		} else {
			(onChange as (next: T | null) => void)(option.value as T);
		}
		setOpen(false);
	};

	const handleClear = () => {
		(onChange as (next: T[] | T | null) => void)(null);
	};

	const selectionCount =
		selected === null ? 0 : Array.isArray(selected) ? selected.length : 1;

	const summary = React.useMemo<string>(() => {
		if (selectionCount === 0) {
			return "All";
		}

		if (!isMulti) {
			const match = options.find((o) =>
				Array.isArray(o.value) ? false : o.value === (selected as T),
			);
			return match?.label ?? String(selected);
		}

		const selectedArray = Array.isArray(selected)
			? selected
			: ([selected] as T[]);

		if (selectedArray.length === 1) {
			const match = options.find((o) =>
				Array.isArray(o.value) ? false : o.value === selectedArray[0],
			);
			return match?.label ?? String(selectedArray[0]);
		}

		return `${selectedArray.length} selected`;
	}, [isMulti, options, selected, selectionCount]);

	const isActive = selected !== null;

	return (
		<DrawerNestedRoot open={open} onOpenChange={setOpen}>
			<DrawerTrigger asChild>
				<FilterRow
					label={label}
					summary={summary}
					isActive={isActive}
					disabled={disabled}
					className={className}
				/>
			</DrawerTrigger>
			<DrawerContent className="max-h-[80vh]">
				<Command className="h-auto overflow-visible bg-transparent">
					<div className="sticky top-0 z-10 bg-background">
						<div className="flex items-center justify-between gap-2 px-4 py-3">
							<DrawerTitle className="text-base font-semibold text-neutral-900">
								{label}
							</DrawerTitle>
							{isActive && (
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={handleClear}
									className="text-primary"
								>
									Clear
								</Button>
							)}
						</div>
						{enableSearch && (
							<CommandInput placeholder={searchPlaceholder} className="h-11" />
						)}
					</div>
					<CommandList className="max-h-none">
						{enableSearch && <CommandEmpty>{noOptionsText}</CommandEmpty>}
						{options.map((option) => {
							const key = Array.isArray(option.value)
								? `${label}-${option.value.join(",")}`
								: `${label}-${option.value}`;
							const selectedItem = isOptionSelected(option);

							const item = (
								<CommandItem
									key={key}
									value={`${option.label}|${Array.isArray(option.value) ? option.value.join(",") : option.value}`}
									onSelect={() => handleSelect(option)}
									className={cn(
										"flex w-full cursor-pointer items-center gap-3 rounded-none border-t border-neutral-200 px-4 py-3 text-sm first:border-t-0",
										"aria-selected:bg-neutral-50",
									)}
								>
									<Checkbox
										checked={selectedItem}
										className="pointer-events-none size-4 shrink-0"
										aria-hidden="true"
										tabIndex={-1}
									/>
									<div className="flex min-w-0 flex-col">
										<span className="truncate" title={option.label}>
											{option.label}
										</span>
										{option.description != null && (
											<span className="truncate text-xs text-neutral-500">
												{option.description}
											</span>
										)}
									</div>
								</CommandItem>
							);

							if (option.renderWrapper) {
								return (
									<React.Fragment key={key}>
										{option.renderWrapper(item)}
									</React.Fragment>
								);
							}
							return item;
						})}
					</CommandList>
				</Command>
				<DrawerFooter className="sticky bottom-0 border-t border-neutral-200 bg-background">
					<DrawerClose asChild>
						<Button type="button" variant="primary">
							Done
						</Button>
					</DrawerClose>
				</DrawerFooter>
			</DrawerContent>
		</DrawerNestedRoot>
	);
}
FilterDrawerSelect.displayName = "FilterDrawerSelect";
